/**
 * Social Profiles Management Module
 *
 * This module initializes and handles the social profiles functionalities including
 * managing Ayrshare JWT token retrieval, social profiles CRUD operations, and calendar
 * configurations for social networks. It tightly integrates with the app's core functionalities
 * and utilizes jQuery for DOM manipulations and AJAX requests.
 *
 * @module aimi_social_profiles
 * @param {Object} aimi_social_profiles - The main object for the social profile functionalities which is used to extend or encapsulate methods and properties.
 * @param {jQuery} $ - The jQuery library used for DOM manipulations and AJAX.
 * @param {Object} app - The central application object which contains methods and properties for the overall app functionality.
 * @param {undefined} [undefined] - An explicit undefined parameter used to safeguard against undefined being overwritten in some JavaScript environments.
 */
(function (aimi_social_profiles, $, app, undefined) {
  let __this = {};

  // Define social API endpoints and methods within the app namespace
  app.apis['social'] = function () {
    return {
      'getAyrshareJwtUrl': function (socialProfileId) {
        let url = `/api/user/ayrshare-jwt-token`;
        if (!app.isEmpty(socialProfileId)) {
          url += `/${socialProfileId}`;
        }
        return app.postRequest(url, {}, function () {
        }, function () {
        })
      }, get: function () {
        return app.getRequest(`/api/user/ayrshare-profile`, {}, function () {
        }, function () {
        })
      }, save: function (socialProfileId) {
        return app.postRequest(`/api/user/ayrshare-profile/${socialProfileId}`, {}, function () {
        }, function () {
        })
      }
    }
  }();

  // Called by ayshare-redirect.html or close window
  // Handles the closing event of Ayrshare social profile and updates the information accordingly
  aimi_social_profiles.onCloseAyrshare = function (socialProfileId) {
    $.when(app.apis['social'].save(socialProfileId)).done(function (response) {
      const $social = aimi_social_profiles.refresh(socialProfileId);
    })
  }

  aimi_social_profiles.checkSocial = async function () {
    if(app.isEmpty(app.calendar)) {
      await app.apis['calendar'].fetch();
    }
    const cal = app.calendar;
    const response = await app.apis['social'].get();
    let socialProfileAccounts = [];
    _.each(response.profiles, function (profile) {
      let accounts = _.isEmpty(profile) ? [] : profile.profileData.activeSocialAccounts;
      _.each(accounts, function (account) {
        socialProfileAccounts.push(account);
      })
    });

    let connected = false, on = false;
    let calSocial = _.isEmpty(cal.config) || _.isEmpty(cal.config.social) ? [] : _.indexBy(cal.config['social'], 'platform');
    // Check Twitter.
    if (_.contains(socialProfileAccounts, 'twitter')) {
      connected = true;
      if (_.has(calSocial, 'twitter')) {
        on = true;
      }
    }
    // Check LinkedIn.
    if (_.contains(socialProfileAccounts, 'linkedin')) {
      connected = true;
      if (_.has(calSocial, 'linkedin')) {
        on = true;
      }
    }

    // Check Facebook.
    if (_.contains(socialProfileAccounts, 'facebook')) {
      connected = true;
      if (_.has(calSocial, 'facebook')) {
        on = true;
      }
    }

    // Check Facebook groups.
    if (_.contains(socialProfileAccounts, 'fbg')) {
      connected = true;
      if (_.has(calSocial, 'fbg')) {
        on = true;
      }
    }

    // Check Google Pages.
    if (_.contains(socialProfileAccounts, 'gmb')) {
      connected = true;
      if (_.has(calSocial, 'gmb')) {
        on = true;
      }
    }

    // Check Pinterest.
    if (_.contains(socialProfileAccounts, 'pinterest')) {
      connected = true;
      if (_.has(calSocial, 'pinterest')) {
        on = true;
      }
    }

    // Check Instagram.
    if (_.contains(socialProfileAccounts, 'instagram')) {
      connected = true;
      if (_.has(calSocial, 'instagram')) {
        on = true;
      }
    }

    let connectionOk = false;

    // Connected [0] == Social network connected in ayrshare
    // Connected [1] == Social turned on in calendar setttings
    if (connected === true && on === false) {
      connectionOk = false;
      app.confirm("Go To Settings", "You have a social network connected, but you need to turn it on for this calendar to post to it.", {'confirmButtonText' : 'Go to Social Profiles'}).then(function (response) {
        const $button = response.button;
        const confirm = response.confirm;
        const $modal = response.modal;

        // If the user confirms, proceed with the social connection.
        if (confirm === true) {
          app.redirect('/social-profiles');
        }
      });
    } else if (connected === false) {
      connectionOk = false;
      app.confirm("Get Started", "In order to post you need to connect a social network to your AIMIAutomation account.", {'confirmButtonText' : 'Go to Social Profiles'}).then(function (response) {
        const $button = response.button;
        const confirm = response.confirm;
        const $modal = response.modal;

        // If the user confirms, proceed with the social connection.
        if (confirm === true) {
          app.redirect('/social-profiles');
        }
      });
    } else if (connected === true && on === true) {
      connectionOk = true;
    }

    return new Promise((resolve, reject) => {
      if (connectionOk === true) return resolve(true);
      if (connectionOk === false) return resolve(false);
    });
  };

  /**
   * Social class responsible for rendering social profiles and managing UI interactions.
   * Extends the generic TableList class to include specific functionality for social profiles.
   */
  class Social extends TableList {
    // Constructor method initializes the Social class
    constructor(app, container) {
      super(app, container, 'social__row', false); // Call the parent class constructor
      this.app = app;                  // Reference to the application object
      this.isRendering = true;         // Flag to indicate if rendering is in progress
      this.container = container;      // The container where the social profiles will be displayed
      this.container.empty();          // Clear any existing content in the container
      this.render();                   // Initiate rendering of social profiles
    }

    loaderRow = {
      id: '',
      title: this.staticPH,
      socialNetworks: [],
      userImage: true,
      platform: '',
      profileUrl: '',
      active: '',
      displayName: '',
      socialProfiles: [{}, {}],
    };

    // Displays a loading animation while data is being fetched
    showLoader(row, loaderRows = 8) {
      let page = _.template(this.app.templates[this.listRow]); // Compile the template for the row
      let $row = $(page(row)).addClass('pre-loader');          // Create a jQuery object and add loading class

      for (let i = 0; i < loaderRows; i++) {
        const $newRow = $row.clone();                          // Clone the loader row
        // Replace placeholder classes with animation classes for visual effect
        $('.as-placeholder-glow', $newRow).removeClass('as-placeholder-glow').addClass('placeholder-glow');
        $('.as-placeholder', $newRow).removeClass('as-placeholder').addClass('placeholder');
        row.socialNetworks.push($newRow[0].outerHTML);         // Add the row HTML to the socialNetworks array
      }

      page = _.template(this.app.templates['social__networks-body']);
      let profile = _.template(this.app.templates['social__networks']);
      profile = profile(row);

      row.socialProfiles.unshift({'profile': profile});

      let $networksBody = $(page(row)).addClass('pre-loader');
      $networksBody.appendTo(this.container);
    }

    // Main rendering function to fetch data and display the social profiles
    async render() {
      this.isRendering = true;
      const self = this;
      this.showLoader(this.loaderRow);

      // Fetch calendar
      await app.apis['calendar'].fetch();

      let response = [];
      try {
        response = await app.apis['social'].get();
      } catch (error) {
        // Handle errors and provide options for retry
        const noRecordsOptions = {
          class: 'available-card-viewport',
          title: 'Error fetching social profiles',
          description: `Please try again in some time`,
          addButton: {
            text: 'Retry',
            onClick: () => {
              self.container.empty();
              self.render();
            }
          },
          help: {
            text: 'Need Help ?',
            onClick: () => {
              console.log('Help clicked');
            }
          },
        };

        this.placeholder = new NoRecordsPlaceholder(app, this.container, noRecordsOptions);
        this.placeholder.render();
      }

      this.removeLoader();
      this.container.empty();

      let $__template = app.templates['social__networks-body'];
      let template = _.template($__template);

      const socialProfiles = [];
      let socialProfilesCount = 1;
      let calendarSocial = [];

      if (app.isEmpty(response)) {
        socialProfilesCount = 1;
        response.profiles = [];
      } else {
        if (app.isEmpty(response.user.socialProfiles)) {
          socialProfilesCount = 1;
          response.profiles = [];
        } else {
          socialProfilesCount = response.user.socialProfiles;
          response.profiles = _.values(response.profiles);
          if (!app.isEmpty(app.calendar['config']) && !app.isEmpty(app.calendar['config'].social))
            calendarSocial = app.calendar['config'].social;
          calendarSocial = _.indexBy(calendarSocial, 'id');
        }
      }

      while (socialProfilesCount-- > 0) {
        let profile = response.profiles.shift();
        const emptyProfile = {id: '', title: '', socialNetworks: []};
        if (app.isEmpty(profile)) {
          profile = emptyProfile;
        } else {
          const profileData = profile.profileData;

          profile = {...profileData, ...emptyProfile, ...{id: profile.profileId, title: profileData.title}};
          profile.socialNetworks = [];

          _.each(profile.displayNames, function (profileRow) {
            console.log(profileRow.id);
            profileRow.active = !app.isEmpty(calendarSocial[profileRow.id]);
            console.log(profileRow.active);
            const socialRow = _.template(app.templates['social__row']);
            const $socialRow = socialRow(profileRow);
            profile.socialNetworks.push($socialRow);
          });
        }

        const networks = _.template(app.templates['social__networks']);
        const profileData = networks(profile);
        socialProfiles.push({...{profile: profileData}, ...profile});
      }

      $(template({'socialProfiles': socialProfiles})).appendTo(this.container);

      this.isRendering = false;
    }
  }

  // Refreshes the social profiles display within the application UI.
  aimi_social_profiles.refresh = (socialProfileId) => {
    const $socialProfilesContainer = $('[data-social-profiles]');
    const $socialManager = new Social(app, $socialProfilesContainer);
    if (socialProfileId) {
      $socialManager.onRenderList().then(function () {
        $socialManager.showTab(`#socialProfile${socialProfileId}`);
      })
    }
    return $socialManager;
  }

  /**
   * Initializes the social profiles management functionality.
   * This method sets up event listeners for social actions and network changes,
   * and initializes components necessary for the UI.
   */
  aimi_social_profiles.init = async function () {
    aimi_social_profiles.refresh();

    const socialActionManager = new EventManager('social-profiles', '[data-social-action]');
    const networkChangeManager = new EventManager('social-profiles', ':input[name="social[]"]');

    /**
     * Handles social profile connect actions triggered by user interaction.
     * @param {Event} e - The event object associated with the social action.
     */
    function socialAction(e) {
      e.preventDefault;

      const $this = $(this);
      const action = $this.data('social-action');

      if (action === 'connect') {
        let $button = $(this);
        app.button($button, 'loading');
        let socialProfileId = $(this).attr('data-id');

        $.when(app.apis['social'].getAyrshareJwtUrl(socialProfileId)).done(function (response) {
          socialProfileId = response.profile.profileId;

          let url = response.url + '&logout=true&redirect=' + window.location.origin + '/aryshare-redirect.html?profile=' + response.profile.profileId;
          let popupWidth = window.outerWidth / 2;
          popupWidth = popupWidth > 1600 ? popupWidth : 1600;

          let popupHeight = window.outerHeight / 2;
          popupHeight = popupHeight > 900 ? popupHeight : 900;

          const childWindow = window.open(url, '_blank', 'resizable=yes, scrollbars=yes, titlebar=yes, height=' + popupHeight + ', width=' + popupWidth + ', left=' + popupWidth / 2 + ', top=' + popupHeight / 2);
          if (!childWindow) {
            app.infoModal('Error', 'Pop up window blocked, please check notification in address bar and allow popup');
          } else {
            // Start checking if the window is closed
            let checkInterval = setInterval(() => {
              if (childWindow.closed) {
                aimi_social_profiles.refresh(socialProfileId);
                clearInterval(checkInterval); // Stop the interval when the window is closed
              }
            }, 500); // Check every 500 milliseconds
          }
          app.button($button, 'reset');
        }).fail(function (response) {
          console.log(response);

          app.infoModal('Error', 'Error creating social profile, please try after some time.');
          app.button($button, 'reset');
        });
      }
    }

    /**
     * Handles changes to the network configuration inputs, updating the calendar with new settings.
     * @param {Event} e - The event object associated with the change action.
     */
    function networkChangeAction(e) {
      const $this = $(this);
      const name = $this.attr('name');
      const $inputs = $(`input[name="${name}"]`);
      let pages = [];
      let socialProfileId = $(this).closest('[data-profileId]').attr('data-profileId');

      const $button = $this.closest('.form-switch');
      app.doButtonAction($button);

      $inputs.each(function () {
        const $this = $(this);
        if ($this.is(':checked')) {
          let social = {
            'platform': $this.data('platform'), 'id': $this.val(), 'name': $this.data('name'),
          };
          pages.push(social);
        }
      });

      const calendar = {...app.calendar, ...{config: {social: pages}}};

      $.when(app.apis['calendar'].update(calendar.id, calendar)).done(function (response) {
        app.doButtonReset($button, true);
        aimi_social_profiles.refresh(socialProfileId);
      });
    }

    socialActionManager.addEventListener('click', socialAction);
    networkChangeManager.addEventListener('change', networkChangeAction);

    app.initSimpleBar(document.getElementById('tab_block_1'));
  }

}(window.aimi_social_profiles = window.aimi_social_profiles || {}, jQuery, PreBuilt));
